import React from 'react';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { AdminUI } from '../../ui/AdminUI';
import EmptyPage from '../EmptyPage/EmptyPage';
import { ProductLoader } from './ProductLoader';
import { IProductInitialValuesProps, ProductValidationSchema } from './types';
import { useProductForm } from './useProductForm';
import { ProductEditorProviders } from './ProductEditorProviders';
import { ProductFormBody } from './ProductFormBody';

export const Product: React.FC = () => {
  const navigate = useNavigate();
  const form = useProductForm();

  if (form.loadingError) {
    return (
      <AdminUI>
        <EmptyPage
          title="This product didn't load"
          description="The connection dropped or the server is busy. Nothing you have saved is affected."
          icon={ExclamationCircleIcon}
          action={{ label: 'Back to products', onAction: () => navigate('/products') }}
        />
      </AdminUI>
    );
  }

  if (form.loading || !form.productInput) {
    return (
      <AdminUI>
        <ProductLoader />
      </AdminUI>
    );
  }

  if (
    form.error ||
    (form.productInput.options && (!form.productInput.variants || form.productInput.variants.length === 0))
  ) {
    return (
      <AdminUI>
        <EmptyPage
          title="This product didn't load"
          description="The connection dropped or the server is busy. Nothing you have saved is affected."
          icon={ExclamationCircleIcon}
          action={{ label: 'Back to products', onAction: () => navigate('/products') }}
        />
      </AdminUI>
    );
  }

  return (
    <AdminUI>
      <ProductEditorProviders>
        <Formik<IProductInitialValuesProps>
          initialValues={form.productInput}
          validationSchema={ProductValidationSchema}
          onSubmit={form.handleSubmit}
          enableReinitialize
        >
          {(props) => <ProductFormBody props={props} form={form} />}
        </Formik>
      </ProductEditorProviders>
    </AdminUI>
  );
};
